import { DASHBOARD_PERMISSIONS } from '../auth/permissions/Dashboard.permissions';
import { USERS_PERMISSIONS } from '../auth/permissions/Users.permissions';
import { APPS_PERMISSIONS } from '../auth/permissions/Apps.permissions';

/**
 * Central PERMISSIONS registry for APFv2 frontend.
 * This file aggregates all modularized permissions.
 */

export const PERMISSIONS = {
  // ── Module Management (Access Only) ──────────────────────────────────────
  DASHBOARD_MANAGE: DASHBOARD_PERMISSIONS.MANAGE,
  FLOWS_MANAGE: 'flows:manage',
  KB_MANAGE: 'knowledge_base:manage',
  APPS_MANAGE: APPS_PERMISSIONS.MANAGE,
  WORKLISTS_MANAGE: 'worklists:manage',
  // AI_MANAGE: 'ai_insight:manage',
  DATA_MODELS_MANAGE: 'data_models:manage',
  DATASOURCES_MANAGE: 'datasources:manage',
  SCHEDULER_MANAGE: 'scheduler:manage',
  ALERTS_MANAGE: 'alerts:manage',
  PARTNERS_MANAGE: 'partners:manage',
  TENANTS_MANAGE: 'tenants:manage',
  USERS_MANAGE: USERS_PERMISSIONS.MANAGE,
  ROLES_MANAGE: 'roles:manage',
  PLANS_MANAGE: 'plans:manage',
  CREDITS_MANAGE: 'credits:manage',
  SUPPORT_MANAGE: 'support:manage',
  SETTINGS_MANAGE: 'settings:manage',
  LOGS_MANAGE: 'activity_logs:manage',
  PLATFORM_SETTINGS_MANAGE: 'platform_settings:manage',
  PUBLIC_DEMO_MANAGE: 'public_demo:manage',

  // ── Tasks & Executions ────────────────────────────────────────────────────
  TASKS_VIEW: 'tasks:view',
  TASKS_MANAGE: 'tasks:manage',
  TASKS_EXPORT: 'tasks:export',
  EXECS_VIEW: 'executions:view',
  EXECS_MANAGE: 'executions:manage',
  EXECS_EXPORT: 'executions:export',

  // ── Dashboard ─────────────────────────────────────────────────────────────
  DASHBOARD_CREATE: DASHBOARD_PERMISSIONS.CREATE,
  DASHBOARD_READ: DASHBOARD_PERMISSIONS.VIEW,
  DASHBOARD_UPDATE: DASHBOARD_PERMISSIONS.UPDATE,
  DASHBOARD_DELETE: DASHBOARD_PERMISSIONS.DELETE,
  DASHBOARD_PUBLISH: DASHBOARD_PERMISSIONS.PUBLISH,
  DASHBOARD_UNPUBLISH: DASHBOARD_PERMISSIONS.UNPUBLISH,
  DASHBOARD_CLONE: DASHBOARD_PERMISSIONS.CLONE,
  DASHBOARD_EXPORT: DASHBOARD_PERMISSIONS.EXPORT,
  DASHBOARD_IMPORT: DASHBOARD_PERMISSIONS.IMPORT,
  DASHBOARD_SHARE: DASHBOARD_PERMISSIONS.SHARE,
  DASHBOARD_SCHEDULE: DASHBOARD_PERMISSIONS.SCHEDULE,
  DASHBOARD_ARCHIVE: DASHBOARD_PERMISSIONS.ARCHIVE,
  DASHBOARD_ASSIGN_TENANT: DASHBOARD_PERMISSIONS.ASSIGN_TENANT,
  DASHBOARD_VIEW_VERSIONS: 'dashboard:view_versions',
  DASHBOARD_RESTORE_VERSION: 'dashboard:restore_version',

  // ── Flows ─────────────────────────────────────────────────────────────────
  FLOWS_CREATE: 'flows:create',
  FLOWS_READ: 'flows:view',
  FLOWS_UPDATE: 'flows:update',
  FLOWS_DELETE: 'flows:delete',
  FLOWS_PUBLISH: 'flows:publish',
  FLOWS_UNPUBLISH: 'flows:unpublish',
  FLOWS_CLONE: 'flows:clone',
  FLOWS_EXPORT: 'flows:export',
  FLOWS_IMPORT: 'flows:import',
  FLOWS_SHARE: 'flows:share',
  FLOWS_SCHEDULE: 'flows:schedule',
  FLOWS_VIEW_VERSIONS: 'flows:view_versions',
  FLOWS_RESTORE_VERSION: 'flows:restore_version',

  // ── Knowledge Base ────────────────────────────────────────────────────────
  KB_CREATE: 'knowledge_base:create',
  KB_READ: 'knowledge_base:view',
  KB_UPDATE: 'knowledge_base:update',
  KB_DELETE: 'knowledge_base:delete',
  KB_PUBLISH: 'knowledge_base:publish',
  KB_UNPUBLISH: 'knowledge_base:unpublish',
  KB_CLONE: 'knowledge_base:clone',
  KB_EXPORT: 'knowledge_base:export',
  KB_IMPORT: 'knowledge_base:import',
  KB_ARCHIVE: 'knowledge_base:archive',
  KB_ASSIGN: 'knowledge_base:assign_tenant',

  // ── Business Apps ─────────────────────────────────────────────────────────
  APPS_CREATE: APPS_PERMISSIONS.CREATE,
  APPS_READ: APPS_PERMISSIONS.VIEW,
  APPS_UPDATE: APPS_PERMISSIONS.UPDATE,
  APPS_DELETE: APPS_PERMISSIONS.DELETE,
  APPS_PUBLISH: APPS_PERMISSIONS.PUBLISH,
  APPS_UNPUBLISH: APPS_PERMISSIONS.UNPUBLISH,
  APPS_CLONE: APPS_PERMISSIONS.CLONE,
  APPS_EXPORT: APPS_PERMISSIONS.EXPORT,
  APPS_IMPORT: APPS_PERMISSIONS.IMPORT,
  BUNDLES_MANAGE: APPS_PERMISSIONS.BUNDLES_MANAGE,
  APPS_ARCHIVE: APPS_PERMISSIONS.ARCHIVE,
  APPS_ASSIGN_PARTNER: APPS_PERMISSIONS.ASSIGN_PARTNER,
  APPS_ASSIGN_TENANT: APPS_PERMISSIONS.ASSIGN_TENANT,
  APPS_UNASSIGN_TENANT: APPS_PERMISSIONS.UNASSIGN_TENANT,


  // ── Worklists ─────────────────────────────────────────────────────────────
  WORKLISTS_CREATE: 'worklists:create',
  WORKLISTS_READ: 'worklists:view',
  WORKLISTS_UPDATE: 'worklists:update',
  WORKLISTS_DELETE: 'worklists:delete',
  WORKLISTS_PUBLISH: 'worklists:publish',
  WORKLISTS_UNPUBLISH: 'worklists:unpublish',
  WORKLISTS_CLONE: 'worklists:clone',
  WORKLISTS_EXPORT: 'worklists:export',
  WORKLISTS_IMPORT: 'worklists:import',


  // ── Agentic Insights ──────────────────────────────────────────────────────
  AI_MANAGE: 'ai_insight:manage',
  AI_READ: 'ai_insight:view',
  AI_DELETE: 'ai_insight:delete',
  AI_EXECUTE: 'ai_insight:execute',
  AI_EXPORT: 'ai_insight:export',
  AI_SHARE: 'ai_insight:share',
  AI_VIEW_ALL: 'ai_insight:view_all_sessions',
  AI_GENERATE_REPORT: 'ai_insight:generate_report',
  AI_MANAGE_SETTINGS: 'ai_insight:manage_settings',
  AI_PIN: 'ai_insight:pin',
  AI_SWITCH_MODEL: 'ai_insight:switch_model',
  AI_VIEW_SQL: 'ai_insight:view_sql',

  // ── Data Models ───────────────────────────────────────────────────────────
  DATA_MODELS_CREATE: 'data_models:create',
  DATA_MODELS_READ: 'data_models:view',
  DATA_MODELS_UPDATE: 'data_models:update',
  DATA_MODELS_DELETE: 'data_models:delete',
  DATA_MODELS_PUBLISH: 'data_models:publish',
  DATA_MODELS_UNPUBLISH: 'data_models:unpublish',
  DATA_MODELS_CLONE: 'data_models:clone',
  DATA_MODELS_EXPORT: 'data_models:export',
  DATA_MODELS_IMPORT: 'data_models:import',
  DATA_MODELS_REFRESH: 'data_models:refresh',
  DATA_MODELS_ARCHIVE: 'data_models:archive',
  DATA_MODELS_ASSIGN: 'data_models:assign_tenant',
  DATA_MODELS_SHARE: 'data_models:share',


  // ── Data Profiler ─────────────────────────────────────────────────────────
  DATA_PROFILE_READ: 'data_profile:view',
  DATA_PROFILE_EXECUTE: 'data_profile:execute',
  DATA_PROFILE_EXPORT: 'data_profile:export',

  // ── Data Sources ──────────────────────────────────────────────────────────
  DATASOURCES_CREATE: 'datasources:create',
  DATASOURCES_READ: 'datasources:view',
  DATASOURCES_UPDATE: 'datasources:update',
  DATASOURCES_DELETE: 'datasources:delete',
  DATASOURCES_EXPORT: 'datasources:export',
  DATASOURCES_IMPORT: 'datasources:import',
  DATASOURCES_TEST: 'datasources:test',

  // ── Scheduler ─────────────────────────────────────────────────────────────
  SCHEDULER_CREATE: 'scheduler:create',
  SCHEDULER_READ: 'scheduler:view',
  SCHEDULER_UPDATE: 'scheduler:update',
  SCHEDULER_DELETE: 'scheduler:delete',
  SCHEDULER_EXPORT: 'scheduler:export',
  SCHEDULER_IMPORT: 'scheduler:import',
  SCHEDULER_EXECUTE: 'scheduler:execute',

  // ── Alerts ────────────────────────────────────────────────────────────────
  ALERTS_CREATE: 'alerts:create',
  ALERTS_READ: 'alerts:view',
  ALERTS_UPDATE: 'alerts:update',
  ALERTS_DELETE: 'alerts:delete',
  ALERTS_EXPORT: 'alerts:export',
  ALERTS_IMPORT: 'alerts:import',
  ALERTS_ACKNOWLEDGE: 'alerts:acknowledge',

  // ── Partners ──────────────────────────────────────────────────────────────
  PARTNERS_CREATE: 'partners:create',
  PARTNERS_READ: 'partners:view',
  PARTNERS_UPDATE: 'partners:update',
  PARTNERS_DELETE: 'partners:delete',
  PARTNERS_EXPORT: 'partners:export',
  PARTNERS_ASSIGN_TENANT: 'partners:assign_tenant',
  PARTNERS_BILLING: 'partners:view_billing',
  PARTNERS_ADJUST_POOL: 'partners:adjust_pool',
  PARTNERS_ALLOCATE_CREDITS: 'partners:allocate_credits',

  // ── Tenants ───────────────────────────────────────────────────────────────
  TENANTS_CREATE: 'tenants:create',
  TENANTS_READ: 'tenants:view',
  TENANTS_UPDATE: 'tenants:update',
  TENANTS_DELETE: 'tenants:delete',
  TENANTS_EXPORT: 'tenants:export',
  TENANTS_IMPORT: 'tenants:import',
  TENANTS_APPROVE: 'tenants:approve',
  TENANTS_ASSIGN_APPS: 'tenants:assign_apps',
  TENANTS_SUSPEND: 'tenants:suspend',
  TENANTS_ASSIGN_PLAN: 'tenants:assign_subscription',
  TENANTS_BILLING: 'tenants:view_billing',
  TENANTS_ONBOARDING_VIEW: 'tenants:onboarding_view',
  TENANTS_ONBOARDING_MANAGE: 'tenants:onboarding_manage',

  // ── Users ─────────────────────────────────────────────────────────────────
  USERS_CREATE: USERS_PERMISSIONS.CREATE,
  USERS_READ: USERS_PERMISSIONS.VIEW,
  USERS_UPDATE: USERS_PERMISSIONS.UPDATE,
  USERS_DELETE: USERS_PERMISSIONS.DELETE,
  USERS_EXPORT: USERS_PERMISSIONS.EXPORT,
  USERS_IMPORT: USERS_PERMISSIONS.IMPORT,
  USERS_ACTIVATE: USERS_PERMISSIONS.ACTIVATE,
  USERS_ASSIGN_ROLE: USERS_PERMISSIONS.ASSIGN_ROLE,
  USERS_IMPERSONATE: USERS_PERMISSIONS.IMPERSONATE,

  // ── Roles & Permissions ───────────────────────────────────────────────────
  ROLES_CREATE: 'roles:create',
  ROLES_READ: 'roles:view',
  ROLES_UPDATE: 'roles:update',
  ROLES_DELETE: 'roles:delete',
  ROLES_EXPORT: 'roles:export',
  ROLES_IMPORT: 'roles:import',
  ROLES_ASSIGN_PERMISSIONS: 'roles:assign_permissions',

  // ── Plans ─────────────────────────────────────────────────────────────────
  PLANS_CREATE: 'plans:create',
  PLANS_READ: 'plans:view',
  PLANS_UPDATE: 'plans:update',
  PLANS_DELETE: 'plans:delete',
  PLANS_EXPORT: 'plans:export',
  PLANS_IMPORT: 'plans:import',
  PLANS_ASSIGN_TENANT: 'plans:assign_tenant',

  // ── Credits ───────────────────────────────────────────────────────────────
  CREDITS_CREATE: 'credits:create',
  CREDITS_READ: 'credits:view',
  CREDITS_UPDATE: 'credits:update',
  CREDITS_DELETE: 'credits:delete',
  CREDITS_EXPORT: 'credits:export',
  CREDITS_IMPORT: 'credits:import',
  CREDITS_ADJUST: 'credits:adjust',

  // ── Support ───────────────────────────────────────────────────────────────
  SUPPORT_CREATE: 'support:create',
  SUPPORT_READ: 'support:view',
  SUPPORT_UPDATE: 'support:update',
  SUPPORT_DELETE: 'support:delete',
  SUPPORT_EXPORT: 'support:export',
  SUPPORT_IMPORT: 'support:import',
  SUPPORT_ASSIGN: 'support:assign_agent',
  SUPPORT_REPLY: 'support:reply',
  SUPPORT_RESOLVE: 'support:resolve',
  SUPPORT_CLOSE: 'support:close',
  SUPPORT_ESCALATE: 'support:escalate',
  SUPPORT_ARCHIVE: 'support:archive',

  // ── Settings & Relay Agents ───────────────────────────────────────────────
  SETTINGS_CREATE: 'settings:create',
  SETTINGS_READ: 'settings:view',
  SETTINGS_UPDATE: 'settings:update',
  SETTINGS_DELETE: 'settings:delete',
  SETTINGS_EXPORT: 'settings:export',
  SETTINGS_IMPORT: 'settings:import',
  SETTINGS_INTEGRATIONS_GLOBAL: 'settings:manage_integrations',
  SETTINGS_INTEGRATIONS: 'settings:manage_integrations',
  SETTINGS_BRANDING: 'settings:manage_branding',
  SETTINGS_SECURITY: 'settings:manage_security',
  SETTINGS_API_KEYS: 'settings:manage_api_keys',
  SETTINGS_PLATFORM_EDIT: 'platform_settings:manage',
  RELAY_AGENTS_VIEW: 'relay_agents:view',
  RELAY_AGENTS_MANAGE: 'relay_agents:manage',
  RELAY_AGENTS_TOKEN: 'relay_agents:manage_token',
  RELAY_AGENTS_EXPORT: 'relay_agents:export',

  // ── Logs ──────────────────────────────────────────────────────────────────
  ACTIVITY_LOGS_READ: 'activity_logs:view',
  ACTIVITY_LOGS_VIEW: 'activity_logs:view',
  AUDIT_LOGS_READ: 'audit_logs:view',
  AUDIT_LOGS_VIEW: 'audit_logs:view',
  AUDIT_LOGS_EXPORT: 'audit_logs:export',
  PLATFORM_SETTINGS_VIEW: 'platform_settings:view',
};
